import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button as DayPickerButton, DayPicker, DayProps, useDayPicker, useDayRender } from "react-day-picker";
import { es } from "date-fns/locale";

import { useTheme } from "@/contexts/ThemeProvider";
import { useSurfaceContrastVariables } from "@/hooks/useSurfaceContrastVariables";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function ContrastAwareDay(props: DayProps) {
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const { tokens } = useTheme();
  const { classNames, styles } = useDayPicker();
  const dayRender = useDayRender(props.date, props.displayMonth, buttonRef);
  const isSelectedOutsideDay = dayRender.activeModifiers.outside && dayRender.activeModifiers.selected;
  const { ref: contrastRef } = useSurfaceContrastVariables<HTMLButtonElement>({
    preferredText: tokens.colors['accent-foreground'] || tokens.colors.foreground,
    disabled: !isSelectedOutsideDay,
  });

  const setRefs = React.useCallback((node: HTMLButtonElement | null) => {
    buttonRef.current = node;
    contrastRef(node);
  }, [contrastRef]);

  if (dayRender.isHidden) {
    return <div role="gridcell"></div>;
  }

  if (!dayRender.isButton) {
    return <div {...dayRender.divProps} />;
  }

  return (
    <DayPickerButton
      {...dayRender.buttonProps}
      ref={setRefs}
      className={cn(
        classNames.button_reset,
        classNames.button,
        dayRender.buttonProps.className,
        isSelectedOutsideDay && "text-[hsl(var(--surface-fg))] opacity-100",
      )}
      style={{
        ...styles.button_reset,
        ...styles.button,
        ...dayRender.buttonProps.style,
      }}
    />
  );
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      locale={es}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4 capitalize",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell:
          "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal aria-selected:opacity-100"
        ),
        day_range_end: "day-range-end",
        day_selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        day_today: "bg-accent text-accent-foreground",
        day_outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:opacity-100",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        Day: ContrastAwareDay,
        IconLeft: ({ ..._props }) => <ChevronLeft className="h-4 w-4" />,
        IconRight: ({ ..._props }) => <ChevronRight className="h-4 w-4" />,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
